import {
  assetRecordListAddressLine,
  assetRecordListTitle,
} from './AssetGetByIdView';
import { AssetImageDistanceOverlay } from './AssetImageDistanceOverlay';
import { AssetImageThumb } from './AssetImageThumb';
import { FavoriteAssetHeartButton } from '../favorites/FavoriteAssetHeartButton';
import { assetRecordLocalizedTypeLabel } from '../i18n/assetTypeLabels';
import { useI18n } from '../i18n';
import {
  assetRecordBookingAvailability,
  assetRecordIsBookable,
} from '../utils/assetBookability';
import { useAssetBrowseThumbs } from '../hooks/useAssetBrowseThumbs';
import { catalogThumbForAssetId } from '../utils/bookingAssetThumbnailCache';
import { coalesceAssetListRecord } from '../utils/assetListNormalize';
import { resolveBookingAssetThumbnailSrc } from '../utils/bookingListDisplay';
import { assetRecordPrimaryId, assetRecordStableListKey } from '../utils/assetRecordPrimaryId';
import { assetRecordSubTitle } from '../utils/assetHomeCardSubtitle';
import styles from './AssetListView.module.css';

type AssetListViewProps = {
  assets: Record<string, unknown>[];
  onSelect: (asset: Record<string, unknown>) => void;
  labelledBy?: string;
  theme?: 'dark' | 'light';
  /**
   * `responsiveGrid`: on wide / fold (≥600px), 1–4 columns by container width.
   * Mobile stays a single-column row list.
   */
  layout?: 'rows' | 'responsiveGrid';
  /** Per-row edit (e.g. owner listing) — opens the update form directly. */
  showEdit?: boolean;
  onEdit?: (asset: Record<string, unknown>) => void | Promise<void>;
  canEditAsset?: (asset: Record<string, unknown>) => boolean;
  editingAssetId?: string | null;
  /** Per-row delete (e.g. owner listing); calls API DELETE then parent refreshes the list. */
  showDelete?: boolean;
  /** When set with `showDelete`, row delete is enabled only if this returns true (e.g. owner match). */
  canDeleteAsset?: (asset: Record<string, unknown>) => boolean;
  onDelete?: (asset: Record<string, unknown>) => void | Promise<void>;
  deletingAssetId?: string | null;
  /** Per-row book (e.g. active assets catalog). Opens Bookings → Create with asset id prefilled. */
  showBook?: boolean;
  onBook?: (asset: Record<string, unknown>) => void;
  /** When true, book buttons stay visible but disabled (e.g. logged-out browse mode). */
  bookDisabled?: boolean;
  bookDisabledReason?: string;
  /** Heart toggle on the thumbnail (browse / search / favorites). */
  showFavoriteHeart?: boolean;
  /** Per-row remove from favorites (favorites tab text button). */
  showUnfavorite?: boolean;
  onUnfavorite?: (asset: Record<string, unknown>) => void;
  /** Owner list: toggle whether the asset is available for booking. */
  showBookingAvailability?: boolean;
  onToggleBookingAvailability?: (
    asset: Record<string, unknown>,
    available: boolean,
  ) => void | Promise<void>;
  bookingAvailabilityBusyId?: string | null;
  canToggleBookingAvailability?: (asset: Record<string, unknown>) => boolean;
};

export function AssetListView({
  assets,
  onSelect,
  labelledBy,
  theme = 'dark',
  layout = 'rows',
  showDelete = false,
  canDeleteAsset,
  onDelete,
  deletingAssetId = null,
  showEdit = false,
  onEdit,
  canEditAsset,
  editingAssetId = null,
  showBook = false,
  onBook,
  bookDisabled = false,
  bookDisabledReason,
  showFavoriteHeart = false,
  showUnfavorite = false,
  onUnfavorite,
  showBookingAvailability = false,
  onToggleBookingAvailability,
  bookingAvailabilityBusyId = null,
  canToggleBookingAvailability,
}: AssetListViewProps) {
  const { t, language } = useI18n();
  const thumbByAssetId = useAssetBrowseThumbs(assets);
  const listClass = [
    styles.list,
    layout === 'responsiveGrid' ? styles.listResponsiveGrid : '',
    theme === 'light' ? styles.lightTheme : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <ul
      className={listClass}
      {...(labelledBy
        ? { 'aria-labelledby': labelledBy }
        : { 'aria-label': t('list.listedAssets') })}
    >
      {assets.map((asset, index) => {
        const title = assetRecordListTitle(asset);
        const addressLine = assetRecordListAddressLine(asset, language);
        const subTitle = assetRecordSubTitle(asset);
        const typeLabel = assetRecordLocalizedTypeLabel(asset, language, t);
        const rowId = assetRecordPrimaryId(asset);
        const embeddedThumb = resolveBookingAssetThumbnailSrc(coalesceAssetListRecord(asset));
        const fetchedThumb = rowId ? catalogThumbForAssetId(thumbByAssetId, rowId) : null;
        // Prefer the row's own image so owner updates are not stuck on a stale catalog thumb.
        const imageSrc = embeddedThumb ?? fetchedThumb;
        const key = assetRecordStableListKey(asset, index);
        const deleteBusy = Boolean(rowId && deletingAssetId === rowId);
        const editBusy = Boolean(rowId && editingAssetId === rowId);
        const availabilityBusy = Boolean(rowId && bookingAvailabilityBusyId === rowId);
        const deleteAllowedByOwner = !canDeleteAsset || canDeleteAsset(asset);
        const editAllowedByOwner = !canEditAsset || canEditAsset(asset);
        const availabilityAllowed =
          !canToggleBookingAvailability || canToggleBookingAvailability(asset);
        const mayDeleteRow = Boolean(rowId && deleteAllowedByOwner);
        const mayEditRow = Boolean(rowId && editAllowedByOwner);
        const mayToggleAvailability = Boolean(rowId && availabilityAllowed);
        const bookingAvailable = assetRecordBookingAvailability(asset);

        return (
          <li key={key} className={styles.item}>
            <article className={styles.card}>
              <div className={styles.media}>
                <AssetImageDistanceOverlay
                  asset={asset}
                  theme={theme}
                  compact
                  className={styles.mediaOverlay}
                >
                  <AssetImageThumb src={imageSrc} className={styles.thumb} />
                </AssetImageDistanceOverlay>
                {showFavoriteHeart ? (
                  <FavoriteAssetHeartButton
                    asset={asset}
                    className={styles.favoriteHeart}
                    activeClassName={styles.favoriteHeartActive}
                  />
                ) : null}
              </div>

              <div className={styles.info}>
                <h3 className={styles.name}>{title}</h3>
                {showEdit && !showBookingAvailability && !assetRecordIsBookable(asset) ? (
                  <p className={styles.unavailableBadge}>{t('assets.statusUnavailableBadge')}</p>
                ) : null}
                {typeLabel ? (
                  <p className={styles.typeChip} aria-label={t('list.assetType')}>
                    {typeLabel}
                  </p>
                ) : null}
                {addressLine ? <p className={styles.address}>{addressLine}</p> : null}
                {subTitle ? <p className={styles.subTitle}>{subTitle}</p> : null}
                {showBookingAvailability && onToggleBookingAvailability ? (
                  <label
                    className={[
                      styles.availabilityToggle,
                      !bookingAvailable ? styles.availabilityToggleOff : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <input
                      type="checkbox"
                      className={styles.availabilityCheckbox}
                      checked={bookingAvailable}
                      disabled={availabilityBusy || !mayToggleAvailability}
                      aria-label={t('assets.availableForBooking')}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        if (!rowId || availabilityBusy || !mayToggleAvailability) {
                          return;
                        }
                        void onToggleBookingAvailability(asset, event.target.checked);
                      }}
                    />
                    <span>{t('assets.availableForBooking')}</span>
                  </label>
                ) : null}
                {showBook && onBook ? (
                  <button
                    type="button"
                    className={styles.orderButton}
                    disabled={bookDisabled || !rowId}
                    title={
                      !rowId
                        ? t('list.cannotBookNoId', { title })
                        : bookDisabled
                          ? bookDisabledReason
                          : undefined
                    }
                    aria-label={
                      bookDisabled
                        ? t('list.cannotBookSignIn', { title })
                        : rowId
                          ? t('list.bookAsset', { title })
                          : t('list.cannotBookNoId', { title })
                    }
                    onClick={() => {
                      if (!bookDisabled && rowId) {
                        onBook(asset);
                      }
                    }}
                  >
                    {t('list.book')}
                  </button>
                ) : null}
              </div>

              <div className={styles.side}>
                {showEdit && onEdit ? (
                  <button
                    type="button"
                    className={styles.editButton}
                    disabled={editBusy || !mayEditRow}
                    aria-label={
                      !rowId
                        ? t('list.cannotEditNoId', { title })
                        : !editAllowedByOwner
                          ? t('list.cannotEditNotOwner', { title })
                          : t('list.editAsset', { title })
                    }
                    title={!editAllowedByOwner ? t('list.ownerOnlyEdit') : undefined}
                    onClick={() => {
                      if (rowId && !editBusy && mayEditRow) {
                        void onEdit(asset);
                      }
                    }}
                  >
                    {editBusy ? '…' : t('list.edit')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className={styles.viewButton}
                  onClick={() => onSelect(asset)}
                  aria-label={t('list.viewAsset', { title })}
                >
                  {t('list.view')}
                </button>
                {showDelete && onDelete ? (
                  <button
                    type="button"
                    className={styles.deleteButton}
                    disabled={deleteBusy || !mayDeleteRow}
                    aria-label={
                      !rowId
                        ? t('list.cannotDeleteNoId', { title })
                        : !deleteAllowedByOwner
                          ? t('list.cannotDeleteNotOwner', { title })
                          : t('list.deleteAsset', { title })
                    }
                    title={!deleteAllowedByOwner ? t('list.ownerOnlyDelete') : undefined}
                    onClick={() => {
                      if (rowId && !deleteBusy && mayDeleteRow) {
                        void onDelete(asset);
                      }
                    }}
                  >
                    {deleteBusy ? '…' : t('common.delete')}
                  </button>
                ) : null}
                {showUnfavorite && onUnfavorite ? (
                  <button
                    type="button"
                    className={styles.unfavoriteButton}
                    aria-label={t('favorites.removeFromList', { title })}
                    onClick={() => onUnfavorite(asset)}
                  >
                    {t('favorites.removeFromListShort')}
                  </button>
                ) : null}
              </div>
            </article>
          </li>
        );
      })}
    </ul>
  );
}
